import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormHelperText,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { isPhoneNumberSelectableForAgent } from '../services/agentService';
import { agentVoiceOptions, DEFAULT_AGENT_VOICE } from '../constants/agentVoices';
import {
  agentLanguageOptions,
  normalizeAgentLanguageCode,
} from '../constants/agentConfig';

const TAB_ORDER = ['general', 'behavior', 'knowledge'];

const TAB_META = {
  general: {
    label: 'Ընդհանուր',
    title: 'Հիմնական կարգավորումներ',
    subtitle: 'Անուն, ողջույն, լեզու, ձայն և կապվող հեռախոսահամար։',
  },
  behavior: {
    label: 'Վարքագիծ',
    title: 'Ագենտի վարքագիծ',
    subtitle: 'Բացատրեք ինչ պետք է անի ագենտը, ինչպես պետք է շփվի և ինչ տոն պետք է պահի։',
  },
  knowledge: {
    label: 'Գիտելիքների բազա',
    title: 'Բիզնեսի գիտելիքների բազա',
    subtitle: 'Ավելացրեք փաստեր, գործընթացներ և կոնտեքստ, որ ագենտը պետք է իմանա։',
  },
};

const FIELD_TO_TAB = {
  name: 'general',
  greeting: 'general',
  languageCode: 'general',
  voice: 'general',
  phoneNumberId: 'general',
  behavior: 'behavior',
  knowledgeBase: 'knowledge',
};

const TAB_FIELDS = {
  general: ['name', 'greeting', 'languageCode', 'voice', 'phoneNumberId'],
  behavior: ['behavior'],
  knowledge: [],
};

const createInitialState = (agent) => ({
  name: agent?.name || '',
  behavior: agent?.behavior || agent?.description || '',
  greeting: agent?.greeting || '',
  languageCode: normalizeAgentLanguageCode(agent?.languageCode),
  voice: agent?.voice || DEFAULT_AGENT_VOICE,
  knowledgeBase: agent?.knowledgeBase || '',
  phoneNumberId: agent?.phoneNumberId || '',
});

const getValidationErrors = ({
  formState,
  availablePhoneNumbers,
  phoneNumbers,
  phoneNumbersLoading,
}) => {
  const errors = {};

  if (!formState.name.trim()) {
    errors.name = 'Մուտքագրեք ագենտի անունը։';
  }

  if (!formState.greeting.trim()) {
    errors.greeting = 'Մուտքագրեք ինչպես պետք է ագենտը դիմավորի զանգահարողին։';
  }

  if (!formState.languageCode) {
    errors.languageCode = 'Ընտրեք լեզուն։';
  }

  if (!formState.voice) {
    errors.voice = 'Ընտրեք ձայնը։';
  }

  if (!formState.behavior.trim()) {
    errors.behavior = 'Նկարագրեք ագենտի վարքագիծը։';
  }

  if (
    !phoneNumbersLoading
    && !formState.phoneNumberId
  ) {
    if (phoneNumbers.length === 0) {
      errors.phoneNumberId = 'Ձեր հաշվում AI հեռախոսահամար դեռ ավելացված չէ։';
    } else if (availablePhoneNumbers.length === 0) {
      errors.phoneNumberId = 'Ազատ հեռախոսահամար չկա այս ագենտի համար։';
    } else {
      errors.phoneNumberId = 'Ընտրեք հեռախոսահամար։';
    }
  }

  if (
    !phoneNumbersLoading
    && formState.phoneNumberId
    && !availablePhoneNumbers.some((phoneNumber) => phoneNumber.id === formState.phoneNumberId)
  ) {
    errors.phoneNumberId = 'Ընտրված համարը հասանելի չէ այս ագենտի համար։';
  }

  return errors;
};

const SectionCard = ({ eyebrow, title, subtitle, children }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: 3,
      border: '1px solid #e2e8f0',
      boxShadow: '0 4px 18px rgba(15, 23, 42, 0.04)',
    }}
  >
    <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
      <Typography
        variant="overline"
        sx={{ letterSpacing: '0.08em', color: '#64748b', fontWeight: 700 }}
      >
        {eyebrow}
      </Typography>
      <Typography variant="h6" sx={{ mt: 0.5, mb: 0.75, fontWeight: 800 }}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
          {subtitle}
        </Typography>
      ) : null}
      {children}
    </CardContent>
  </Card>
);

const AgentSetupFlow = ({
  mode = 'create',
  agent = null,
  phoneNumbers = [],
  phoneNumbersLoading = false,
  submitting = false,
  onCancel,
  onDelete,
  onSubmit,
}) => {
  const [activeTab, setActiveTab] = useState('general');
  const [formState, setFormState] = useState(createInitialState(agent));
  const [showValidation, setShowValidation] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const fieldRefs = useRef({});

  const currentAgentId = agent?.id || '';
  const isCreateMode = mode === 'create';
  const activeTabIndex = TAB_ORDER.indexOf(activeTab);
  const isLastCreateStep = activeTabIndex === TAB_ORDER.length - 1;
  const menuOpen = Boolean(menuAnchorEl);

  const availablePhoneNumbers = useMemo(
    () => phoneNumbers.filter((phoneNumber) => isPhoneNumberSelectableForAgent(phoneNumber, currentAgentId)),
    [currentAgentId, phoneNumbers]
  );

  const validationErrors = useMemo(
    () => getValidationErrors({
      formState,
      availablePhoneNumbers,
      phoneNumbers,
      phoneNumbersLoading,
    }),
    [availablePhoneNumbers, formState, phoneNumbers, phoneNumbersLoading]
  );

  useEffect(() => {
    setActiveTab('general');
    setFormState(createInitialState(agent));
    setShowValidation(false);
    setMenuAnchorEl(null);
    setDeleteDialogOpen(false);
    setDeleteConfirmation('');
  }, [agent, mode]);

  useEffect(() => {
    if (formState.phoneNumberId || availablePhoneNumbers.length !== 1) {
      return;
    }

    setFormState((previous) => ({
      ...previous,
      phoneNumberId: availablePhoneNumbers[0].id,
    }));
  }, [availablePhoneNumbers, formState.phoneNumberId]);

  const handleFieldChange = (field) => (event) => {
    setFormState((previous) => ({
      ...previous,
      [field]: event.target.value,
    }));
  };

  const focusField = (field) => {
    const target = fieldRefs.current[field];

    if (!target) {
      return;
    }

    if (typeof target.focus === 'function') {
      target.focus();
      return;
    }

    const inputNode = target.querySelector?.('input, textarea, [tabindex]');
    inputNode?.focus?.();
  };

  const focusFirstInvalidField = (fieldNames) => {
    const firstInvalidField = fieldNames.find((field) => Boolean(validationErrors[field]));

    if (!firstInvalidField) {
      return false;
    }

    setShowValidation(true);
    setActiveTab(FIELD_TO_TAB[firstInvalidField]);
    requestAnimationFrame(() => focusField(firstInvalidField));
    return true;
  };

  const handleSubmit = () => {
    setShowValidation(true);

    const fieldOrder = ['name', 'greeting', 'languageCode', 'voice', 'phoneNumberId', 'behavior', 'knowledgeBase'];
    const firstInvalidField = fieldOrder.find((field) => Boolean(validationErrors[field]));

    if (firstInvalidField) {
      setActiveTab(FIELD_TO_TAB[firstInvalidField]);
      requestAnimationFrame(() => focusField(firstInvalidField));
      return;
    }

    onSubmit({
      name: formState.name.trim(),
      behavior: formState.behavior.trim(),
      greeting: formState.greeting.trim(),
      languageCode: formState.languageCode,
      voice: formState.voice,
      knowledgeBase: formState.knowledgeBase.trim(),
      phoneNumberId: formState.phoneNumberId,
    });
  };

  const handleNext = () => {
    const fieldsForCurrentTab = TAB_FIELDS[activeTab] || [];

    if (fieldsForCurrentTab.length > 0 && focusFirstInvalidField(fieldsForCurrentTab)) {
      return;
    }

    const nextTab = TAB_ORDER[Math.min(activeTabIndex + 1, TAB_ORDER.length - 1)];
    setActiveTab(nextTab);
  };

  const handleBack = () => {
    if (activeTabIndex <= 0) {
      onCancel?.();
      return;
    }

    setActiveTab(TAB_ORDER[activeTabIndex - 1]);
  };

  const renderPhoneNumberPicker = () => {
    if (phoneNumbersLoading) {
      return (
        <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      );
    }

    if (phoneNumbers.length === 0) {
      return (
        <Card
          variant="outlined"
          sx={{
            borderRadius: 3,
            borderStyle: 'dashed',
            backgroundColor: '#f8fafc',
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Ձեր հաշվում համար դեռ չկա
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
              Երբ ձեր հաշվի համար AI համարը ավելացվի, այստեղից անմիջապես կկարողանաք կապել այն ագենտի հետ։
            </Typography>
          </CardContent>
        </Card>
      );
    }

    return (
      <Stack spacing={1.5}>
        {phoneNumbers.map((phoneNumber) => {
          const isSelectable = isPhoneNumberSelectableForAgent(phoneNumber, currentAgentId);
          const isSelected = formState.phoneNumberId === phoneNumber.id;

          return (
            <Card
              key={phoneNumber.id}
              variant="outlined"
              onClick={() => {
                if (!isSelectable || submitting) {
                  return;
                }

                setFormState((previous) => ({
                  ...previous,
                  phoneNumberId: phoneNumber.id,
                }));
              }}
              sx={{
                borderRadius: 3,
                cursor: isSelectable && !submitting ? 'pointer' : 'not-allowed',
                borderColor: isSelected ? '#94a3b8' : '#dbe5f0',
                backgroundColor: isSelected ? '#f8fafc' : '#ffffff',
                opacity: isSelectable ? 1 : 0.62,
                transition: 'all 0.2s ease',
                '&:hover': isSelectable && !submitting ? {
                  borderColor: '#94a3b8',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 12px 24px rgba(15, 23, 42, 0.06)',
                } : undefined,
              }}
            >
              <CardContent sx={{ p: 2.25 }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box sx={{ mt: 0.35, color: isSelected ? '#475569' : 'text.secondary' }}>
                    {isSelected ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mb: 0.75 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {phoneNumber.phoneNumber}
                      </Typography>
                      <Box
                        sx={{
                          px: 1.25,
                          py: 0.4,
                          borderRadius: 999,
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          color: isSelectable ? '#166534' : '#991b1b',
                          backgroundColor: isSelectable ? '#dcfce7' : '#fee2e2',
                        }}
                      >
                        {isSelectable ? 'Հասանելի է' : 'Արդեն կցված է'}
                      </Box>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {phoneNumber.label || 'Առանց անվան'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {isSelectable
                        ? 'Այս համարը կօգտագործվի զանգերը այս ագենտի հետ կապելու համար։'
                        : `Կցված է «${phoneNumber.assignedAgentName || 'Անանուն ագենտ'}» ագենտին։`}
                    </Typography>
                  </Box>
                  {!isSelectable ? <LockOutlinedIcon color="disabled" /> : null}
                </Stack>
              </CardContent>
            </Card>
          );
        })}

        {availablePhoneNumbers.length === 0 ? (
          <Alert severity="warning" sx={{ borderRadius: 3 }}>
            Ձեր բոլոր համարները արդեն կցված են այլ ագենտների։
          </Alert>
        ) : null}
      </Stack>
    );
  };

  const renderGeneralTab = () => (
    <Stack spacing={3}>
      <SectionCard
        eyebrow="Հիմնական տվյալներ"
        title="Ագենտի ինքնությունը"
        subtitle="Սահմանեք ինչպես պետք է ներկայացվի ագենտը և ինչով պետք է սկսի յուրաքանչյուր զանգը։"
      >
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={6}>
            <TextField
              inputRef={(node) => {
                fieldRefs.current.name = node;
              }}
              autoFocus
              fullWidth
              required
              label="Ագենտի անուն"
              value={formState.name}
              onChange={handleFieldChange('name')}
              error={showValidation && Boolean(validationErrors.name)}
              helperText={showValidation ? validationErrors.name : 'Օրինակ՝ Վաճառքի օգնական կամ Հերթապահ ընդունարան։'}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              required
              label="Լեզու"
              value={formState.languageCode}
              onChange={handleFieldChange('languageCode')}
              error={showValidation && Boolean(validationErrors.languageCode)}
              helperText={showValidation && validationErrors.languageCode
                ? validationErrors.languageCode
                : 'Այս պահին ագենտը աշխատում է հայերեն ռեժիմով։'}
              inputRef={(node) => {
                fieldRefs.current.languageCode = node;
              }}
            >
              {agentLanguageOptions.map((languageOption) => (
                <MenuItem key={languageOption.value} value={languageOption.value} sx={{ py: 1.25, fontWeight: 600 }}>
                  {languageOption.title}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              inputRef={(node) => {
                fieldRefs.current.greeting = node;
              }}
              fullWidth
              required
              label="Ողջույնի խոսք"
              multiline
              minRows={3}
              value={formState.greeting}
              onChange={handleFieldChange('greeting')}
              error={showValidation && Boolean(validationErrors.greeting)}
              helperText={showValidation && validationErrors.greeting
                ? validationErrors.greeting
                : 'Օրինակ՝ «Բարև, շնորհակալ եմ զանգահարելու համար։ Ինչո՞վ կարող եմ օգնել»։'}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              required
              label="Ձայն"
              value={formState.voice}
              onChange={handleFieldChange('voice')}
              error={showValidation && Boolean(validationErrors.voice)}
              helperText={showValidation && validationErrors.voice
                ? validationErrors.voice
                : 'Ընտրեք այն ձայնը, որով ագենտը կխոսի զանգերի ընթացքում։'}
              inputRef={(node) => {
                fieldRefs.current.voice = node;
              }}
            >
              {agentVoiceOptions.map((voiceOption) => (
                <MenuItem key={voiceOption.value} value={voiceOption.value} sx={{ py: 1.25 }}>
                  <ListItemText
                    primary={voiceOption.title}
                    secondary={voiceOption.subtitle}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard
        eyebrow="Հեռախոսահամար"
        title="Կապվող համարը"
        subtitle="Ընտրեք այն համարը, որի վրա եկող զանգերը պետք է սպասարկի այս ագենտը։"
      >
        <Box
          ref={(node) => {
            fieldRefs.current.phoneNumberId = node;
          }}
        >
          {renderPhoneNumberPicker()}
        </Box>
        {showValidation && validationErrors.phoneNumberId ? (
          <FormHelperText error sx={{ mt: 1.5, mx: 0 }}>
            {validationErrors.phoneNumberId}
          </FormHelperText>
        ) : null}
      </SectionCard>
    </Stack>
  );

  const renderBehaviorTab = () => (
    <SectionCard
      eyebrow="Վարքագիծ"
      title="Ինչպե՞ս պետք է իրեն պահի ագենտը"
      subtitle="Գրեք հրահանգներ, որոնք կբացատրեն դերը, շփման ոճը, առաջնահերթությունները և սահմանափակումները։"
    >
      <TextField
        inputRef={(node) => {
          fieldRefs.current.behavior = node;
        }}
        fullWidth
        required
        multiline
        minRows={12}
        label="Վարքագիծ"
        value={formState.behavior}
        onChange={handleFieldChange('behavior')}
        error={showValidation && Boolean(validationErrors.behavior)}
        helperText={showValidation && validationErrors.behavior
          ? validationErrors.behavior
          : 'Նկարագրեք ինչ պետք է անի ագենտը, ինչպես պետք է խոսի, ինչ հարցեր տա և ինչից պետք է խուսափի։'}
      />
    </SectionCard>
  );

  const renderKnowledgeTab = () => (
    <SectionCard
      eyebrow="Գիտելիքներ"
      title="Տրամադրեք կոնտեքստ"
      subtitle="Ավելացրեք ծառայությունների, գների, գործընթացների, թիմի կամ հաճախ տրվող հարցերի մասին տեղեկություն։"
    >
      <TextField
        inputRef={(node) => {
          fieldRefs.current.knowledgeBase = node;
        }}
        fullWidth
        multiline
        minRows={14}
        label="Գիտելիքների բազա"
        value={formState.knowledgeBase}
        onChange={handleFieldChange('knowledgeBase')}
        helperText="Այս բաժինը պարտադիր չէ, բայց լավ լրացված կոնտեքստը զգալիորեն բարելավում է ագենտի պատասխանների որակը։"
      />
    </SectionCard>
  );

  const currentMeta = TAB_META[activeTab];

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid #e2e8f0',
          backgroundColor: '#ffffff',
          boxShadow: '0 4px 18px rgba(15, 23, 42, 0.04)',
        }}
      >
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Stack spacing={2.5}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <IconButton
                  onClick={isCreateMode ? handleBack : onCancel}
                  disabled={submitting}
                  sx={{
                    bgcolor: 'grey.100',
                    border: '1px solid #e2e8f0',
                    '&:hover': { bgcolor: 'grey.200' },
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.1, color: '#0f172a' }}>
                    {mode === 'edit'
                      ? `Խմբագրել «${agent?.name || 'AI ագենտ'}»`
                      : 'Ստեղծել AI ագենտ'}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mt: 0.75 }}>
                    {isCreateMode
                      ? `Քայլ ${activeTabIndex + 1} / ${TAB_ORDER.length}`
                      : 'Խմբագրեք կարգավորումները և պահպանեք փոփոխությունները։'}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center" sx={{ width: { xs: '100%', md: 'auto' }, justifyContent: { xs: 'flex-end', md: 'flex-end' } }}>
                {!isCreateMode ? (
                  <IconButton
                    onClick={(event) => setMenuAnchorEl(event.currentTarget)}
                    disabled={submitting}
                    sx={{
                      bgcolor: 'grey.100',
                      border: '1px solid #e2e8f0',
                      '&:hover': { bgcolor: 'grey.200' },
                    }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                ) : null}
              </Stack>
            </Stack>

            <Box
              sx={{
                borderRadius: 3,
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                px: { xs: 1, md: 1.5 },
                py: 0.75,
              }}
            >
              <Tabs
                value={activeTab}
                onChange={(_, nextValue) => setActiveTab(nextValue)}
                variant="scrollable"
                scrollButtons="auto"
                TabIndicatorProps={{ style: { display: 'none' } }}
                sx={{
                  '& .MuiTab-root': {
                    color: '#64748b',
                    textTransform: 'none',
                    fontWeight: 700,
                    minHeight: 52,
                    minWidth: 120,
                    borderRadius: 2,
                    transition: 'all 0.2s ease',
                  },
                  '& .Mui-selected': {
                    color: '#0f172a',
                    backgroundColor: '#ffffff',
                  },
                }}
              >
                {TAB_ORDER.map((tabKey) => (
                  <Tab key={tabKey} value={tabKey} label={TAB_META[tabKey].label} />
                ))}
              </Tabs>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {activeTab === 'general' ? renderGeneralTab() : null}
      {activeTab === 'behavior' ? renderBehaviorTab() : null}
      {activeTab === 'knowledge' ? renderKnowledgeTab() : null}

      <Box
        sx={{
          position: 'sticky',
          bottom: 12,
          zIndex: 5,
          pb: { xs: 1, md: 2 },
        }}
      >
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            boxShadow: '0 12px 24px rgba(15, 23, 42, 0.08)',
            backdropFilter: 'blur(12px)',
            backgroundColor: 'rgba(255,255,255,0.96)',
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
              <Stack spacing={0.5}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  {isCreateMode
                    ? `Քայլ ${activeTabIndex + 1} / ${TAB_ORDER.length}`
                    : 'Պահպանեք թարմացված կոնֆիգուրացիան'}
                </Typography>
                <Typography color="text.secondary">
                  {isCreateMode
                    ? currentMeta.subtitle
                    : 'Փոփոխությունները կկիրառվեն ագենտի հետագա զանգերի համար։'}
                </Typography>
              </Stack>
              <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.25}>
                {isCreateMode ? (
                  <Button
                    variant="text"
                    onClick={handleBack}
                    disabled={submitting}
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    {activeTabIndex === 0 ? 'Չեղարկել' : 'Հետ'}
                  </Button>
                ) : (
                  <Button
                    variant="text"
                    onClick={onCancel}
                    disabled={submitting}
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    Չեղարկել
                  </Button>
                )}
                <Button
                  variant="contained"
                  size="large"
                  onClick={isCreateMode ? (isLastCreateStep ? handleSubmit : handleNext) : handleSubmit}
                  disabled={submitting}
                  startIcon={submitting && (isLastCreateStep || !isCreateMode) ? <CircularProgress size={18} color="inherit" /> : null}
                  endIcon={isCreateMode && !isLastCreateStep ? <ArrowForwardIcon /> : null}
                  sx={{
                    minWidth: 220,
                    py: 1.35,
                    borderRadius: 3,
                    textTransform: 'none',
                    fontWeight: 800,
                  }}
                >
                  {isCreateMode
                    ? (isLastCreateStep ? 'Ստեղծել ագենտ' : 'Հաջորդը')
                    : 'Պահպանել կարգավորումը'}
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Box>
      </Box>

      <Menu anchorEl={menuAnchorEl} open={menuOpen} onClose={() => setMenuAnchorEl(null)}>
        <MenuItem onClick={() => { setDeleteDialogOpen(true); setMenuAnchorEl(null); }} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteOutlineIcon fontSize="small" color="error" /></ListItemIcon>
          Ջնջել ագենտը
        </MenuItem>
      </Menu>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Հաստատե՞լ ագենտի ջնջումը</DialogTitle>
        <DialogContent>
          <DialogContentText paragraph>
            Սա անդառնալի գործողություն է։ Ագենտը ջնջելուց հետո այդ համարի հետ կապված կոնֆիգուրացիան կկորչի, և համարը նորից կդառնա հասանելի այլ ագենտների համար։
          </DialogContentText>
          <DialogContentText paragraph>
            Շարունակելու համար, խնդրում ենք մուտքագրել ագենտի անունը՝ <strong>{agent?.name || 'AI ագենտ'}</strong>
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            value={deleteConfirmation}
            onChange={(event) => setDeleteConfirmation(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Չեղարկել</Button>
          <Button
            color="error"
            onClick={() => onDelete?.(agent)}
            disabled={deleteConfirmation !== (agent?.name || 'AI ագենտ') || submitting}
          >
            Ես հասկանում եմ, ջնջել
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AgentSetupFlow;
